import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { T, TONES, MANROPE } from "../clientsTokens";

/* Shared furniture for the client record view (Clients directive Part B §20). */

export const Card = ({ children, testid }) => (
  <div className="rounded-[12px] overflow-hidden" data-testid={testid}
    style={{ background: T.card, border: `1px solid ${T.border}`, boxShadow: "0 1px 2px rgba(26,26,26,0.04)" }}>
    {children}
  </div>
);

export const CardHead = ({ title, count, note, action }) => (
  <div className="flex items-center gap-2.5 px-4 py-3" style={{ borderBottom: `1px solid ${T.dividerInner}` }}>
    <span style={{ ...MANROPE, fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</span>
    {count !== undefined && count !== null && (
      <span className="mono text-[11px]" style={{ color: T.ink2 }}>{count}</span>
    )}
    {note && <span className="text-[11.5px] truncate" style={{ color: T.ink2 }}>{note}</span>}
    {action && <div className="ml-auto shrink-0">{action}</div>}
  </div>
);

export const Secondary = ({ children, onClick, href, testid, title }) => {
  const cls = "h-7 px-2.5 rounded-[7px] text-[12px] font-semibold inline-flex items-center gap-1.5 whitespace-nowrap hover:bg-[#F6F4EF]";
  const style = { background: T.card, border: `1px solid ${T.control}`, color: T.ink2 };
  return href
    ? <a href={href} className={cls} style={style} data-testid={testid} title={title}>{children}</a>
    : <button onClick={onClick} className={cls} style={style} data-testid={testid} title={title}>{children}</button>;
};

export const Pill = ({ text, tone = "neutral" }) => {
  const c = TONES[tone] || TONES.neutral;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap"
      style={{ padding: "1px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 700,
        background: c.fill, color: c.ink, border: `1px solid ${c.border}` }}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: c.dot }} />{text}
    </span>
  );
};

export const Tag = ({ children }) => (
  <span className="whitespace-nowrap" style={{ padding: "1px 7px", borderRadius: 4, fontSize: 10,
    fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
    background: "#F1EEE6", border: `1px solid ${T.border}`, color: "#59554D" }}>{children}</span>
);

export const Dash = () => <span style={{ color: T.disabled }}>—</span>;

/* One menu open at a time; escape, outside click and scroll close it (§26.5).
   Rendered through a portal at fixed coordinates: cards clip their content
   (overflow-hidden), which used to cut the menu off at the card edge. */
export const DotsMenu = ({ items, testid, size = 30, label }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const pop = useRef(null);
  const WIDTH = 272;

  const place = () => {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    const rows = items.filter(Boolean).length;
    const height = rows * 44 + 12;
    const below = window.innerHeight - r.bottom;
    setPos({
      left: Math.max(8, Math.min(r.right - WIDTH, window.innerWidth - WIDTH - 8)),
      top: below < height + 12 ? Math.max(8, r.top - height - 2) : r.bottom + 2,
    });
  };

  useEffect(() => {
    if (!open) return;
    const off = (e) => {
      if (!btn.current?.contains(e.target) && !pop.current?.contains(e.target)) setOpen(false);
    };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", close, { once: true, capture: true });
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", off);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", close, { capture: true });
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <div className="shrink-0">
      <button ref={btn} data-testid={testid} aria-label={label || "More"}
        onClick={() => { if (!open) place(); setOpen(!open); }}
        className="rounded-[7px] flex items-center justify-center hover:bg-[#F6F4EF]"
        style={{ width: size, height: size, color: T.ink2 }}>
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && pos && createPortal(
        <div ref={pop} className="fixed rounded-[12px] p-1.5" style={{
          left: pos.left, top: pos.top, width: WIDTH, zIndex: 80, background: T.panel,
          border: `1px solid ${T.border}`, boxShadow: "0 12px 32px rgba(26,26,26,0.16)" }}>
          {items.filter(Boolean).map((it) => (
            <button key={it.label} data-testid={it.testid}
              onClick={() => { setOpen(false); it.onClick?.(); }}
              className="w-full text-left rounded-[8px] px-2.5 py-2 hover:bg-[#F4F2ED] block">
              <div className="text-[13px] font-semibold" style={{ color: it.danger ? T.destructive : T.ink }}>
                {it.label}
              </div>
              {it.hint && <div className="text-[11.5px] leading-snug mt-0.5" style={{ color: T.ink2 }}>{it.hint}</div>}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
};

export const Skel = ({ w = "100%", h = 13 }) => (
  <div className="animate-pulse rounded-[4px]" style={{ width: w, height: h, background: T.sunken }} />
);
